import { useToast } from '@repo/design-system/index'
import { QIcon_information_filled } from '@repo/icons/index'
import { useQuery } from '@tanstack/react-query'
import classNames from 'classnames'
import { useContext, useEffect, useState } from 'react'
import { useEventTracking } from '../../../../helpers/gtmHelper'
import apis from '../../../../modules/apis'
import { useMaterialsByCategoryId } from '../../../../modules/apis/student/material/queries'
import { axiosGet } from '../../../../modules/axiosHelper'
import { STACK_ACTIVITY_NAME } from '../../../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../../../modules/hooks/common/useAppRouter'
import useIsMobile from '../../../../modules/hooks/common/useIsMobile'
import { useOnclickHeartPacket } from '../../../../modules/hooks/common/useOnclickHeartPacket'
import { n_material, n_upcoming_lessons } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import urls from '../../../../modules/urls'
import { COLORS } from '../../../../modules/vars'
import { SelectMaterialContext } from '../../../../pages/[lang]/portal/lessons/materials/[lessonId]'
import { useLessonsStack } from '../../../../pages/[lang]/webview/lessons'
import { useMaterialsStack } from '../../../../pages/[lang]/webview/materials'
import SelectMaterialCard, { SelectMaterialCardSkeleton } from './SelectMaterialCard'

const fetchLessonInfo = async (lessonId) => {
  const response = await axiosGet(apis.lessons.upcoming(lessonId))

  if (response) return response.data
}

const fetchTutorInfo = async (tutorId) => {
  const response = await axiosGet(
    apis.matching.materials.filterObj({
      filter: 'tutor_prev',
      detail: false,
      tutor_id: tutorId,
    })
  )

  if (response) return response
}
const PrevHourMaterialSelect = () => {
  const FREE_WRITING_MATERIALS_ID = 19
  const TEMP_TUTOR_PREV_MATERIALS_ID = 3333
  const { t, isLocaleKO } = useTranslation()

  const [categoryId, setCategoryId] = useState(TEMP_TUTOR_PREV_MATERIALS_ID)
  const [selectedHashId, setSelectedHashId] = useState('All')

  const hashTags = [
    {
      id: TEMP_TUTOR_PREV_MATERIALS_ID,
      title: t(n_upcoming_lessons.edit_materials.tutor_prev_material),
    },
    {
      id: FREE_WRITING_MATERIALS_ID,
      title: t(n_upcoming_lessons.edit_materials.free_writing_material),
    },
  ]

  const handleHashButton = (id: number) => {
    setCategoryId(id)
    setSelectedHashId('All')
  }

  const isMobile = useIsMobile(null, 614)
  const router = useAppRouter()
  const { sendClickEvent } = useEventTracking()
  const { toastSuccess } = useToast()
  const { selectedCourse, setSelectedCourse, lessonId } = useContext(SelectMaterialContext)
  const lessonSelectLessonId = lessonId ? lessonId : router.query.lessonId
  const [tutorId, setTutorId] = useState()

  const [materialData, setMaterialData] = useState({ small_hashtags: [] })
  const [materialList, setMaterialList] = useState([])

  const { useLessonsFlow } = useLessonsStack()
  const { push: lessonPush } = useLessonsFlow()
  const lessonRouter = useAppRouter({ stackPush: lessonPush })

  const { useMaterialsFlow } = useMaterialsStack()
  const { push: materialsPush } = useMaterialsFlow()
  const materialsRouter = useAppRouter({ stackPush: materialsPush })

  const titleTranslations = (data) => [
    data.title_kr,
    data.title_en,
    '',
    data.title_zh_chs,
    data.title_zh_cht,
    data.title_ja,
    data.title_vi,
  ]

  // ----------------------DATA FETCHING--------------------
  const { data: lessonInfoData, isFetching }: any = useQuery({
    queryKey: ['lesson_data', router.query.lessonId],
    queryFn: () => fetchLessonInfo(router.query.lessonId),
  })

  const { data: tutorData, isFetching: tutorDataFetching }: any = useQuery({
    queryKey: ['tutor_prev_materials', tutorId],
    queryFn: () => fetchTutorInfo(tutorId),
    enabled: !!tutorId,
  })

  useEffect(() => {
    if (lessonInfoData && !isFetching) {
      setTutorId(lessonInfoData.tutor.id)
    }
  }, [lessonInfoData, isFetching])

  const { data: categoryMaterialData, isFetching: categoryMaterialsIsFetching } =
    useMaterialsByCategoryId(FREE_WRITING_MATERIALS_ID)

  useEffect(() => {
    if (categoryMaterialData && !categoryMaterialsIsFetching) {
      setMaterialData(categoryMaterialData)
      const filteredCourseList =
        selectedHashId && categoryId != 21
          ? categoryMaterialData.small_hashtags
              ?.filter((hash) => hash.title_en == selectedHashId)
              .flatMap((hash) => hash.courses) || []
          : categoryMaterialData.small_hashtags[0]?.courses
      setMaterialList(filteredCourseList)
    }
  }, [categoryMaterialData, categoryMaterialsIsFetching, selectedHashId])
  // -------------------------------------------------------------

  const onClickHeartPacket = useOnclickHeartPacket({
    packetItems: materialList,
    setPacketItems: setMaterialList,
    isList: true,
    onAdded: () => toastSuccess(t(n_material.toast_book_mark)),
    onDeleted: () => toastSuccess(t(n_material.toast_delete_book_mark)),
  })

  const handleClickSelect = (course) => {
    const isSelected = course.id === selectedCourse?.id

    if (isSelected) {
      setSelectedCourse(undefined)
    } else {
      setSelectedCourse(course)
    }
  }

  const filteredList =
    tutorData && !tutorDataFetching && categoryId == TEMP_TUTOR_PREV_MATERIALS_ID ? tutorData.courses : materialList
  return (
    <div className="">
      <div className="mt-4 flex w-full flex-col">
        <div className="hidden w-full flex-row items-center gap-[4px] rounded-[4px] bg-purple-50 p-[12px] sm:flex">
          <QIcon_information_filled width={20} height={20} color={COLORS.PURPLE400} />
          <div className="Body2-14Bd">{t(n_upcoming_lessons.edit_materials.info_new)}</div>
        </div>
        <div className="mt-[8px] flex flex-col gap-x-[32px] sm:mt-[20px] sm:flex-row">
          {/* 사이드 카테고리 */}
          <div className="flex flex-row gap-[8px] text-gray-500 sm:flex-col">
            {hashTags.map((hashTag) => {
              return (
                <button
                  className={classNames(
                    hashTag.id === Number(categoryId ? categoryId : 0) &&
                      'border-b-[2px] border-black text-black lg:border-b-0 lg:bg-purple-50 lg:text-purple-500',
                    'Body1-16Bd lg:Body2-14Bd flex items-center justify-start whitespace-nowrap py-[8px] px-[12px] lg:rounded-[70px]'
                  )}
                  key={hashTag.id}
                  onClick={() => handleHashButton(hashTag.id)}
                >
                  {hashTag.title}
                </button>
              )
            })}
          </div>
          <div className="Body2-14Md mt-[20px] mb-[16px] flex w-full flex-row items-center gap-x-12 rounded-[4px] bg-purple-50 p-[12px] sm:hidden">
            <QIcon_information_filled width={20} height={20} color={COLORS.PURPLE400} />
            <div className="">{t(n_upcoming_lessons.edit_materials.info_new)}</div>
          </div>
          {/* 교재 리스트 */}
          <div className="w-full">
            <div className={classNames('flex flex-col')}>
              {/* 카테고리 교재 목록 */}
              {materialData && !categoryMaterialsIsFetching ? (
                <>
                  {tutorData && !tutorDataFetching && categoryId == TEMP_TUTOR_PREV_MATERIALS_ID ? (
                    <div className="Headline2-24Bd">{isLocaleKO ? tutorData.title_kr : tutorData.title_en}</div>
                  ) : (
                    <div className="Headline2-24Bd">
                      {t(titleTranslations(categoryMaterialData))} (
                      {categoryMaterialData?.small_hashtags[0]?.courses?.length})
                    </div>
                  )}

                  <div className="mt-[16px] flex flex-wrap gap-[8px] border-gray-300 font-medium">
                    {categoryId !== TEMP_TUTOR_PREV_MATERIALS_ID &&
                      categoryMaterialData?.small_hashtags?.map((hash, i) => {
                        return (
                          <button
                            key={i}
                            onClick={() => setSelectedHashId(hash.title_en)}
                            className={classNames(
                              selectedHashId == hash.title_en &&
                                'border-purple-500 bg-purple-50 text-[16px] font-bold text-purple-500',
                              'rounded-[8px] border-[1.5px] px-[16px] py-[8px] text-[16px] '
                            )}
                          >
                            {isLocaleKO ? hash.title_kr : hash.title_en}
                          </button>
                        )
                      })}
                  </div>
                  <div
                    className={classNames('md:grid-cols-3', 'mt-[36px] grid grid-cols-1 gap-y-[10px] sm:grid-cols-2')}
                  >
                    {filteredList?.map((course) => {
                      return (
                        <div
                          className=""
                          key={course.id}
                          onClick={() => {
                            sendClickEvent({ button_name: 'material_card', object_id: course.id })
                            router.asPath.includes('webview/materials')
                              ? materialsRouter.push(
                                  urls.student.newPortal.materials.detail(course.id) +
                                    `?lessonId=${lessonSelectLessonId}`,
                                  {
                                    stackActivity: STACK_ACTIVITY_NAME.MATERIAL_DETAIL_ACTIVITY,
                                    stackSendData: {
                                      materialId: course.id,
                                      lessonId: lessonSelectLessonId,
                                    },
                                  }
                                )
                              : lessonRouter.push(
                                  urls.student.newPortal.materials.detail(course.id) +
                                    `?lessonId=${lessonSelectLessonId}`,
                                  {
                                    stackActivity: STACK_ACTIVITY_NAME.LESSON_MATERIAL_DETAIL_ACTIVITY,
                                    stackSendData: {
                                      materialId: course.id,
                                      lessonId: lessonSelectLessonId,
                                    },
                                  }
                                )
                          }}
                        >
                          <SelectMaterialCard
                            horizontal={isMobile}
                            title={course.title}
                            subtitle={course.subtitle}
                            is_mine={course.is_mine}
                            category={course.category}
                            image_url={course.image_url}
                            is_revised={course.is_revised}
                            is_today={course.is_today}
                            selected={course.id === selectedCourse?.id}
                            onClickSelect={() => handleClickSelect(course)}
                            onClickHeart={(e) => onClickHeartPacket(e, course)}
                            level={course.level}
                            audioLength={course.mp3_running_time_in_minute}
                          />
                        </div>
                      )
                    })}
                  </div>
                </>
              ) : (
                <>
                  <div className="border-1 h-[34px] w-[240px] animate-pulse rounded-[4px] bg-gray-100"></div>
                  <div className=" mt-[16px] flex animate-pulse flex-wrap gap-[8px] border-gray-300 font-medium">
                    <div className="flex flex-row gap-[16px]">
                      <button
                        className={
                          'border-1 h-[42px] w-[64px] rounded-[8px] bg-gray-100 px-[16px] py-[8px] text-[16px]'
                        }
                      ></button>
                      <button
                        className={
                          'border-1 h-[42px] w-[64px] rounded-[8px] bg-gray-100 px-[16px] py-[8px] text-[16px]'
                        }
                      ></button>
                      <button
                        className={
                          'border-1 h-[42px] w-[64px] rounded-[8px] bg-gray-100 px-[16px] py-[8px] text-[16px]'
                        }
                      ></button>
                    </div>
                  </div>
                  <div className="w-full animate-pulse">
                    {[0, 1, 2, 3, 4, 5].map((item, i) => {
                      return (
                        <div className="mb-[32px] mt-[36px] w-full" key={i}>
                          <section className="">
                            <div
                              className={classNames(
                                'md:grid-cols-3 lg:w-[688px]',
                                'flex w-full flex-row gap-[9px] sm:grid sm:grid-cols-2 '
                              )}
                            >
                              {[0, 1, 2].map((_, i) => (
                                <div className="w-full" key={i}>
                                  <SelectMaterialCardSkeleton />
                                </div>
                              ))}
                            </div>
                          </section>
                        </div>
                      )
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PrevHourMaterialSelect
